/**
 * Edit an internal (Teamwork) member from the Admin Panel Users tab (O41/O42):
 * the display name the Agile Plan shows and an admin note. The department is
 * read-only (Teamwork Job Roles, O37). `onSave` receives only the changed fields;
 * a blank value clears the field.
 */
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { TeamworkMemberPatch } from '@/services/authApi';

const OVERRIDE_MAX = 80;
const NOTE_MAX = 500;

export interface InternalMemberDialogMember {
  teamwork_person_id: string;
  name: string;
  display_name_override: string | null;
  note: string | null;
  department: string | null;
}

export function InternalMemberDialog({
  open, onOpenChange, member, onSave, saving = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: InternalMemberDialogMember;
  onSave: (changes: Pick<TeamworkMemberPatch, 'display_name_override' | 'note'>) => void;
  saving?: boolean;
}) {
  const [override, setOverride] = useState(member.display_name_override ?? '');
  const [note, setNote] = useState(member.note ?? '');
  const [error, setError] = useState('');

  const save = () => {
    const trimmed = override.trim();
    if (trimmed.length > OVERRIDE_MAX) {
      setError(`The display name can be at most ${OVERRIDE_MAX} characters.`);
      return;
    }
    if (note.length > NOTE_MAX) {
      setError(`The note can be at most ${NOTE_MAX} characters.`);
      return;
    }
    setError('');
    const changes: Pick<TeamworkMemberPatch, 'display_name_override' | 'note'> = {};
    if (trimmed !== (member.display_name_override ?? '')) changes.display_name_override = trimmed;
    if (note !== (member.note ?? '')) changes.note = note;
    if (Object.keys(changes).length) onSave(changes);
    else onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit internal member</DialogTitle>
          <DialogDescription>
            {member.name}. The display name is what the Agile Plan shows; tasks still save the Teamwork name.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="internal-member-override">Display name</Label>
            <Input
              id="internal-member-override"
              value={override}
              placeholder={member.name}
              onChange={(e) => setOverride(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="internal-member-note">Note</Label>
            <Textarea id="internal-member-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
          </div>
          <p className="text-sm">
            <span className="text-muted-foreground">Department (from Teamwork): </span>
            <span>{member.department ?? '—'}</span>
          </p>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
